import { action, computed, makeObservable, override, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { redirectToRoute } from 'routes/redirectToRoute';
import { QuoteRouteV2Id } from 'routes/routesConfigV2';
import QuoteFlowDelegate from 'store/flows/quote/quoteFlowDelegate';
import IEmploymentDetailsPage from 'store/modules/subscriber/employment-details/iEmploymentDetailsPage';
import { completeSubscriberChecklistStep, saveGeneralIMQAnswers } from 'api/operations/electionService';
import { SubChecklistCard } from 'types/electionService';
import { Primitive } from 'types/general';
import { ImqBrokerHealthQuestionnaireDomainParams } from '../health-questionnaire-entry/pageStore';

export type ImqBrokerEmploymentDetailsDomainParams = ImqBrokerHealthQuestionnaireDomainParams;

export default class ImqBrokerEmploymentDetailsPageStore extends IEmploymentDetailsPage {
  protected readonly _domainParams: ImqBrokerEmploymentDetailsDomainParams;
  public readonly flowDelegate: QuoteFlowDelegate;

  protected onCompleteRedirect(): void {
    const { groupId, periodId, cartId } = this.domainParams;
    redirectToRoute(QuoteRouteV2Id.IMQ_ADD_EMPLOYEES, { groupId, periodId, cartId });
  }

  public override get domainParams(): ImqBrokerEmploymentDetailsDomainParams {
    return this._domainParams;
  }

  public get isInitialLoading(): boolean {
    return this.flowDelegate.isFlowDataLoading || this.isPageContentLoading;
  }

  public override async completeForm(): Promise<void> {
    await saveGeneralIMQAnswers(
      this._domainParams.subscriberId,
      this.dynamicFormDelegate.formAnswers as Record<string, Primitive>,
    );
    await runInAction(async () => {
      await completeSubscriberChecklistStep({
        subId: this._domainParams.subscriberId,
        entityId: this._domainParams.subscriberId,
        card: SubChecklistCard.IMQ_GENERAL_INFO,
      });
    });

    runInAction(() => {
      this.onCompleteRedirect();
    });
  }

  public returnBack(): void {
    const { groupId, periodId, cartId } = this.domainParams;
    redirectToRoute(QuoteRouteV2Id.IMQ_ADD_EMPLOYEES, { groupId, periodId, cartId });
  }

  public async getFlowAndPageInitialData(): Promise<void> {
    this.flowDelegate.setIsInitialDataLoading(true);
    await this.flowDelegate.getFlowInitialData();
    await this.getInitialData();
    this.flowDelegate.setIsInitialDataLoading(false);
  }

  constructor(domainParams: ImqBrokerEmploymentDetailsDomainParams) {
    super(domainParams);
    this._domainParams = domainParams;
    this.flowDelegate = new QuoteFlowDelegate({
      domainParams: {
        groupId: domainParams.groupId,
        periodId: domainParams.periodId,
        cartId: domainParams.cartId,
      },
      nextPageId: QuoteRouteV2Id.IMQ_ADD_EMPLOYEES,
    });

    makeObservable<ImqBrokerEmploymentDetailsPageStore, 'onCompleteRedirect'>(this, {
      isInitialLoading: computed,
      domainParams: override,
      onCompleteRedirect: action.bound,
      returnBack: action.bound,
      getFlowAndPageInitialData: action.bound,
    });
  }
}

const ImqBrokerEmploymentDetailsPageStoreContext = createContext(
  new ImqBrokerEmploymentDetailsPageStore({} as ImqBrokerEmploymentDetailsDomainParams),
);

export const useImqBrokerEmploymentDetailsPageStore = (): ImqBrokerEmploymentDetailsPageStore => {
  return useContext(ImqBrokerEmploymentDetailsPageStoreContext);
};

export { ImqBrokerEmploymentDetailsPageStoreContext };
